import React from 'react';

import i18n from 'util/i18n';
import * as formTypes from 'constants/formTypes';

import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceDrugsUsageItemModel } from 'forms/sections/substance/drugs/usage/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import { isFormSF852013 } from 'helpers/form';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Text from 'components/Form/Text';
import DrugType from './DrugType';
import Dropdown from 'components/Form/Dropdown';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';

interface Props extends ValidationElementProps, Partial<SubstanceDrugsUsageItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  requireDrugWhileSafety?: boolean
  requireDrugWithClearance?: boolean
  requireDrugInFuture?: boolean
  formType: formTypes.FormTypes
  formVersion: string
}
export default class DrugUseItem extends ValidationElement<Props> {
  update = (values: Partial<SubstanceDrugsUsageItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        DrugType: this.props.DrugType,
        FirstUse: this.props.FirstUse,
        RecentUse: this.props.RecentUse,
        NatureOfUse: this.props.NatureOfUse,
        UseWhileEmployed: this.props.UseWhileEmployed,
        UseWithClearance: this.props.UseWithClearance,
        UseInFuture: this.props.UseInFuture,
        Explanation: this.props.Explanation,
        Comments: this.props.Comments,
        DrugTypeOther: this.props.DrugTypeOther,
        HowManyTimes: this.props.HowManyTimes,
        Reason: this.props.Reason,
        UseWhileEmployedPosition: this.props.UseWhileEmployedPosition,
        UseWhileEmployedExplanation: this.props.UseWhileEmployedExplanation,
        UseWithClearanceExplanation: this.props.UseWithClearanceExplanation,
        UseInFutureExplanation: this.props.UseInFutureExplanation,
        ...values,
      });
    }
  };

  updateDrugType = (values: SubstanceDrugsUsageItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateFirstUse = (values: SubstanceDrugsUsageItemModel['FirstUse']) => {
    this.update({ FirstUse: values });
  };

  updateRecentUse = (values: SubstanceDrugsUsageItemModel['RecentUse']) => {
    this.update({ RecentUse: values });
  };

  updateNatureOfUse = (values: SubstanceDrugsUsageItemModel['NatureOfUse']) => {
    this.update({ NatureOfUse: values });
  };

  updateUseWhileEmployed = (values: SubstanceDrugsUsageItemModel['UseWhileEmployed']) => {
    this.update({ UseWhileEmployed: values });
  };

  updateUseWithClearance = (values: SubstanceDrugsUsageItemModel['UseWithClearance']) => {
    this.update({ UseWithClearance: values });
  };

  updateUseInFuture = (values: SubstanceDrugsUsageItemModel['UseInFuture']) => {
    this.update({ UseInFuture: values });
  };

  updateExplanation = (values: SubstanceDrugsUsageItemModel['Explanation']) => {
    this.update({ Explanation: values });
  };

  updateComments = (values: SubstanceDrugsUsageItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsUsageItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateHowManyTimes = (values: SubstanceDrugsUsageItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceDrugsUsageItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  updateUseWhileEmployedPosition = (values: SubstanceDrugsUsageItemModel['UseWhileEmployedPosition']) => {
    this.update({ UseWhileEmployedPosition: values });
  };

  updateUseWhileEmployedExplanation = (values: SubstanceDrugsUsageItemModel['UseWhileEmployedExplanation']) => {
    this.update({ UseWhileEmployedExplanation: values });
  };

  updateUseWithClearanceExplanation = (values: SubstanceDrugsUsageItemModel['UseWithClearanceExplanation']) => {
    this.update({ UseWithClearanceExplanation: values });
  };

  updateUseInFutureExplanation = (values: SubstanceDrugsUsageItemModel['UseInFutureExplanation']) => {
    this.update({ UseInFutureExplanation: values });
  };

  render() {
    const { formType, formVersion } = this.props;
    const { requireDrugWhileSafety, requireDrugWithClearance, requireDrugInFuture } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-use">
        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.drugType')}
          className="drug-type-use"
          attribute={`${attribute}.DrugType`}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            {...this.props.DrugType}
            attribute={attribute}
            name="DrugType"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
          />
        </Field>

        {this.props.DrugType?.value === 'Other' && (

          <Field
            title={i18n.t('substance.pvq.drugs.use.heading.otherDrugType')}
            attribute={`${attribute}.DrugTypeOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.DrugTypeOther}
              name="DrugTypeOther"
              attribute={`${attribute}.DrugTypeOther`}
              className="drugTypeOther"
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}
        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.firstUse')}
          attribute={`${attribute}.FirstUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.FirstUse}
            name="FirstUse"
            attribute={`${attribute}.FirstUse`}
            className="first-use"
            hideDay
            minDateEqualTo
            onUpdate={this.updateFirstUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.recentUse')}
          attribute={`${attribute}.RecentUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.RecentUse}
            name="RecentUse"
            attribute={`${attribute}.RecentUse`}
            className="recent-use"
            hideDay
            minDate={this.props.FirstUse}
            prefix="drugUsage"
            minDateEqualTo
            onUpdate={this.updateRecentUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Branch
          name="UseWithClearance"
          attribute={`${attribute}.UseWithClearance`}
          label={i18n.t('substance.pvq.drugs.use.heading.useWithClearance')}
          labelSize="h4"
          className="use-with-clearance"
          {...this.props.UseWithClearance}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWithClearance}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.UseWithClearance?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.drugs.use.heading.useWithClearanceExplanation')}
            attribute={`${attribute}.UseWithClearanceExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.UseWithClearanceExplanation}
              name="useWhileEmployedExplanation"
              attribute={`${attribute}.UseWithClearanceExplanation`}
              className="useWithClearanceExplanation"
              onUpdate={this.updateUseWithClearanceExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}


        <Branch
          {...this.props.UseWhileEmployed}
          name="UseWhileEmployed"
          attribute={`${attribute}.UseWhileEmployed`}
          label={i18n.t('substance.pvq.drugs.use.heading.useWhileEmployed')}
          labelSize="h4"
          className="use-while-employed"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWhileEmployed}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.UseWhileEmployed?.value === 'Yes' && (
          <>

            <Field
              title={i18n.t('substance.pvq.drugs.use.heading.useWhileEmployedPosition')}
              attribute={`${attribute}.UseWhileEmployedPosition`}
              adjustFor="buttons"
              shrink
              className="no-margin-bottom"
            >
              <Dropdown
                attribute={`${attribute}.UseWhileEmployedPosition`}
                name="UseWhileEmployedPosition"
                className="UseWhileEmployedPosition"
                onUpdate={this.updateUseWhileEmployedPosition}
                onError={this.props.onError}
                value={this.props.UseWhileEmployedPosition?.value}
                required={this.props.required}
              >
                {criminalJusticeorPublicSafetyPosition.map((s) => {
                  return (<option key={s} value={s}>
                    {s}
                  </option>);
                })}
              </Dropdown>
            </Field>
            <Field
              title={i18n.t('substance.pvq.drugs.use.heading.useWhileEmployedExplanation')}
              attribute={`${attribute}.UseWhileEmployedExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.UseWhileEmployedExplanation}
                name="useWhileEmployedExplanation"
                attribute={`${attribute}.UseWhileEmployedExplanation`}
                className="useWhileEmployedExplanation"
                onUpdate={this.updateUseWhileEmployedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </>
        )}

       
        <Branch
          {...this.props.UseInFuture}
          name="UseInFuture"
          attribute={`${attribute}.UseInFuture`}
          label={i18n.t('substance.pvq.drugs.use.heading.useInFuture')}
          labelSize="h4"
          className="use-in-future"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseInFuture}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Field
          title={i18n.t('substance.pvq.drugs.use.heading.useInFutureExplanation')}
          attribute={`${attribute}.UseInFutureExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >

          <Textarea
            {...this.props.UseInFutureExplanation}
            name="UseInFutureExplanation"
            attribute={`${attribute}.UseInFutureExplanation`}
            className="useInFutureExplanation"
            onUpdate={this.updateUseInFutureExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
      </div>
    ) : (
      <div className="drug-use">
        <Field
          title={i18n.t('substance.drugs.use.heading.drugType')}
          className="drug-type-use"
          attribute={`${attribute}.DrugType`}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            attribute={attribute}
            name="DrugType"
            {...this.props.DrugType}
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.use.heading.firstUse')}
          attribute={`${attribute}.FirstUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="FirstUse"
            attribute={`${attribute}.FirstUse`}
            className="first-use"
            {...this.props.FirstUse}
            hideDay
            minDateEqualTo
            onUpdate={this.updateFirstUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.use.heading.recentUse')}
          attribute={`${attribute}.RecentUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="RecentUse"
            attribute={`${attribute}.RecentUse`}
            className="recent-use"
            {...this.props.RecentUse}
            hideDay
            minDate={this.props.FirstUse}
            prefix="drugUsage"
            minDateEqualTo
            onUpdate={this.updateRecentUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.use.heading.natureOfUse')}
          attribute={`${attribute}.NatureOfUse`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="NatureOfUse"
            attribute={`${attribute}.NatureOfUse`}
            className="nature-of-use"
            {...this.props.NatureOfUse}
            onUpdate={this.updateNatureOfUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        {requireDrugWhileSafety && (
          <Branch
            name="UseWhileEmployed"
            attribute={`${attribute}.UseWhileEmployed`}
            label={i18n.t('substance.drugs.use.heading.useWhileEmployed')}
            labelSize="h4"
            className="use-while-employed"
            {...this.props.UseWhileEmployed}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateUseWhileEmployed}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {requireDrugWithClearance && (
          <Branch
            name="UseWithClearance"
            attribute={`${attribute}.UseWithClearance`}
            label={i18n.t('substance.drugs.use.heading.useWithClearance')}
            labelSize="h4"
            className="use-with-clearance"
            {...this.props.UseWithClearance}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateUseWithClearance}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {requireDrugInFuture && (
          <span>
            <Branch
              name="UseInFuture"
              attribute={`${attribute}.UseInFuture`}
              label={i18n.t('substance.drugs.use.heading.useInFuture')}
              labelSize="h4"
              className="use-in-future"
              {...this.props.UseInFuture}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateUseInFuture}
              scrollIntoView={this.props.scrollIntoView}
            />

            <Field
              title={i18n.t('substance.drugs.use.heading.explanation')}
              attribute={`${attribute}.Explanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="Explanation"
                attribute={`${attribute}.Explanation`}
                className="explanation"
                {...this.props.Explanation}
                onUpdate={this.updateExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </span>
        )}

        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="Comments"
            className="optional-comment"
            attribute="Comments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}
      </div>
    );
  }
}

DrugUseItem.defaultProps = {
  UseWhileEmployed: {},
  UseWhileClearance: {},
  UseWhileFuture: {},
  Comments: {},
  DrugTypeOther: {},
  HowManyTimes: {},
  Reason: {},
  UseWhileEmployedPosition: {},
  UseWhileEmployedExplanation: {},
  UseWithClearanceExplanation: {},
  UseInFutureExplanation: {},
  onError: (value, arr: ErrorCode[]) => arr,
};
